import React, { useState, useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ActivityIndicator } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';

import BasicLayout from '../../common/components/CustomLayout/BasicLayout';
import TouchActionGroup from '../../common/components/navpanel/TouchActionGroup';
import TouchNavGroup from '../../common/components/navpanel/TouchNavGroup';
import { getTempActionsData, updateTempAction } from '../../api/Settings';

interface Props {
    navigation: any;
    initialItem?: string;
}

interface TempAction {
    id: string;
    title: string;
    value: boolean;
    icon: string;
}

const Automation = ({navigation, initialItem }:Props) => {
    const [selectedItem, setSelectedItem] = useState<string>(initialItem || '');
    const [tempActions, setTempActions] = useState<TempAction[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    
    useEffect(() => {
        loadTempActionsData();
    }, []);

    const loadTempActionsData = async () => {
        setLoading(true);
        setError('');
        try {
            await getTempActionsData((jsonResponse, success, error) => {
                if (success && jsonResponse) {
                    const mappedActions = jsonResponse.map((action: any) => ({
                        id: action.id,
                        title: action.name,
                        value: action.status,
                        icon: action.icon || 'cog'
                    }));
                    setTempActions(mappedActions);
                } else {
                    setError(error || 'Failed to load temp actions data');
                }
                setLoading(false);
            });
        } catch (err) {
            setError('An error occurred while loading data');
            setLoading(false);
        }
    };

    const handleTempActionToggle = async (actionId: string, currentValue: boolean) => {
        const newValue = !currentValue;
        
        // Optimistically update the UI
        setTempActions(prev => 
            prev.map(action => 
                action.id === actionId 
                    ? { ...action, value: newValue }
                    : action
            )
        );

        // Update on server
        try {
            await updateTempAction(
                { id: actionId, status: newValue },
                (jsonResponse, success, error) => {
                    if (!success) {
                        // Revert the optimistic update on failure
                        setTempActions(prev => 
                            prev.map(action => 
                                action.id === actionId 
                                    ? { ...action, value: currentValue }
                                    : action
                            )
                        );
                        setError(error || 'Failed to update temp action');
                    }
                }
            );
        } catch (err) {
            // Revert the optimistic update on error
            setTempActions(prev => 
                prev.map(action => 
                    action.id === actionId 
                        ? { ...action, value: currentValue }
                        : action
                )
            );
            setError('An error occurred while updating');
        }
    };
    
    const handleItemClick = (itemName) => {
        setSelectedItem(itemName);
    };
    
    if(selectedItem) {
        switch (selectedItem) {
            case 'Address': 
                navigation.navigate('AddressAutomation'); 
                break;
            default:
                return (
                    <View style={{ marginTop: 20, paddingHorizontal: 10, paddingVertical:2, height: 28, justifyContent: 'center', flexDirection: 'row',}}>
                        <TouchableOpacity onPress={() => handleItemClick(null)}>
                            <Text>{'< Back'}</Text>
                        </TouchableOpacity>
                        <Text style={{ fontSize: 28 }}>{selectedItem}</Text>
                    </View>
                );
        }
    };

    return (
        <BasicLayout
            navigation={navigation}
            screenName={"Automation"}
        >
            <ScrollView>
                <View style={styles.container}>
                    <TouchNavGroup
                        sectionTitle="Automations" 
                        items={[
                            { title: "Address", icon: 'map-marker-alt' },
                        ]} 
                        handleItemClick={handleItemClick} 
                    />

                    <TouchActionGroup
                        sectionTitle="Actions"
                        items={tempActions}
                        handleItemToggle={handleTempActionToggle}
                        showSwitches={true}
                        loading={loading}
                        error={error}
                    />
                </View>
            </ScrollView> 
        </BasicLayout>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        padding: 20,
        width: '90%',
        maxWidth: 1000,
        margin: 'auto',
        marginTop: 40,
    },
});

export default Automation;